import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, FileText } from 'lucide-react';

export const COMPANY = {
  name: 'Smart7 Digital Ltda.',
  cnpj: '19.416.207/0001-70',
  address: 'R. Grajaú, 120 — Quadra 85, Lote 13',
  district: 'Jd. Guanabara',
  city: 'Goiânia',
  state: 'GO',
  zip: '74675-340',
  dpo_email: 'dpo@meadd.app',
  contact_email: 'contato@meadd.app',
};

const TABS = [
  { id: 'privacy', label: 'Política de Privacidade', icon: ShieldCheck, path: '/privacidade' },
  { id: 'terms', label: 'Termos de Uso', icon: FileText, path: '/termos' },
];

export const LegalLayout = ({ activeTab, lastUpdated, title, intro, children }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-white font-manrope text-slate-800">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center" data-testid="legal-logo">
            <img src="/brand/meadd-logo.png" alt="MeAdd" className="h-12" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium group"
            data-testid="legal-back"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Voltar para o site
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
        {/* Tabs */}
        <div className="flex gap-2 mb-10">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <Link
                key={t.id}
                to={t.path}
                data-testid={`legal-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  active
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </Link>
            );
          })}
        </div>

        {/* Header section */}
        <div className="mb-10 pb-8 border-b border-slate-200">
          <h1 className="font-outfit font-bold text-4xl md:text-5xl tracking-tight text-slate-900">
            {title}
          </h1>
          {intro && (
            <p className="text-lg text-slate-600 mt-4 leading-relaxed max-w-3xl">{intro}</p>
          )}
          {lastUpdated && (
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-4">
              Última atualização: {lastUpdated}
            </p>
          )}
        </div>

        {/* Content */}
        <article className="legal-content space-y-8 max-w-3xl">{children}</article>

        {/* Company footer */}
        <div className="mt-16 pt-8 border-t border-slate-200 text-sm text-slate-600 space-y-1.5">
          <div className="font-semibold text-slate-900">{COMPANY.name}</div>
          <div>CNPJ: {COMPANY.cnpj}</div>
          <div>{COMPANY.address} · {COMPANY.district}</div>
          <div>{COMPANY.city}/{COMPANY.state} — CEP {COMPANY.zip}</div>
          <div className="pt-2">
            Contato: <a href={`mailto:${COMPANY.contact_email}`} className="text-emerald-700 hover:underline">{COMPANY.contact_email}</a> · DPO/Encarregado: <a href={`mailto:${COMPANY.dpo_email}`} className="text-emerald-700 hover:underline">{COMPANY.dpo_email}</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const LegalSection = ({ id, title, children }) => (
  <section id={id} className="space-y-3 scroll-mt-24">
    <h2 className="font-outfit font-bold text-2xl text-slate-900 tracking-tight">{title}</h2>
    <div className="text-slate-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default LegalLayout;
